import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import { z } from 'zod'

dayjs.extend(utc)

export const ANALYTICS_TTL = 60 * 60 * 1000
export const reportDimensionSchema = z.enum(['organization', 'customer', 'salesperson', 'site', 'business', 'subject'])
export const reportFilterFields = {
  organization: 'organizationIds',
  customer: 'customerIds',
  salesperson: 'salespersonIds',
  site: 'siteIds',
  business: 'businessIds',
  subject: 'subjectIds'
} as const
const ids = z
  .array(z.coerce.number().int().positive().max(2147483647))
  .max(100)
  .transform((values) => [...new Set(values)])
  .optional()
const reportQueryBase = z.object({
  month: z
    .string()
    .regex(/^(20\d{2})-(0[1-9]|1[0-2])$/)
    .optional(),
  dimension: reportDimensionSchema.default('organization'),
  id: z.coerce.number().int().positive().max(2147483647).optional(),
  dateFrom: z.iso.date().optional(),
  dateTo: z.iso.date().optional(),
  granularity: z.enum(['day', 'month']).optional(),
  orderKind: z.enum(['rental', 'service']).optional(),
  organizationIds: ids,
  customerIds: ids,
  salespersonIds: ids,
  siteIds: ids,
  businessIds: ids,
  subjectIds: ids
})
function validateRange(input: z.infer<typeof reportQueryBase>, ctx: z.RefinementCtx) {
  const issue = (message: string) => ctx.addIssue({ code: 'custom', path: ['dateTo'], message })
  if (!!input.dateFrom !== !!input.dateTo) {
    issue('起止日期必须同时提供')
  }
  if (input.dateFrom && input.dateTo) {
    if (input.month) {
      issue('月份与自定义日期范围不能同时提供')
    }
    const days = dayjs.utc(input.dateTo).diff(dayjs.utc(input.dateFrom), 'day') + 1
    if (days < 1 || days > 366) {
      issue('日期范围须为 1 至 366 天')
    }
  }
}
export const reportQuerySchema = reportQueryBase.superRefine(validateRange)
const reportListBase = reportQueryBase.extend({
  current: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  keyword: z.string().trim().max(100).optional(),
  sortField: z
    .enum([
      'orders',
      'billed',
      'received',
      'outstanding',
      'activeRentals',
      'expense',
      'paid',
      'paymentRefund',
      'cashBalance',
      'ledgerBalance'
    ])
    .default('orders'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  activeOnly: z.boolean().optional()
})
export const reportListSchema = reportListBase.superRefine(validateRange)
export const reportSearchSchema = reportListBase
  .omit({ dimension: true })
  .extend({ view: z.enum(['charts', 'table']).optional() })
  .superRefine((input, ctx) => validateRange({ ...input, dimension: 'organization' }, ctx))
export const reportOptionsSchema = z.object({
  dimension: reportDimensionSchema,
  keyword: z.string().trim().max(100).optional(),
  selectedIds: ids
})
export type ReportQuery = z.infer<typeof reportQuerySchema>
export type ReportListQuery = z.infer<typeof reportListSchema>
export type ReportDimension = z.infer<typeof reportDimensionSchema>
const money = z.string()
export const reportMetricsSchema = z.object({
  orders: z.number(),
  customers: z.number(),
  subjects: z.number(),
  billed: money,
  received: money,
  refunded: money,
  expense: money,
  paid: money,
  paymentRefund: money,
  cashBalance: money,
  ledgerBalance: money,
  net: money
})
export const reportPointSchema = reportMetricsSchema.extend({ label: z.string() })
export const reportRowSchema = reportMetricsSchema.extend({
  id: z.number().nullable(),
  name: z.string(),
  outstanding: money,
  previousOrders: z.number(),
  yearOrders: z.number(),
  activeRentals: z.number()
})
export type ReportMetrics = z.infer<typeof reportMetricsSchema>
export type ReportRow = z.infer<typeof reportRowSchema>
export type ReportPoint = z.infer<typeof reportPointSchema>
export const reportSummarySchema = z.object({
  generatedAt: z.string(),
  month: z.string(),
  through: z.string(),
  periods: z.object({
    current: z.object({ start: z.string(), end: z.string() }),
    previous: z.object({ start: z.string(), end: z.string() }),
    year: z.object({ start: z.string(), end: z.string() })
  }),
  granularity: z.enum(['day', 'month']),
  activity: z.object({ current: z.number(), previous: z.number(), year: z.number() }),
  current: reportMetricsSchema,
  previous: reportMetricsSchema,
  year: reportMetricsSchema,
  outstanding: money,
  activeRentals: z.number(),
  daily: z.array(reportPointSchema),
  history: z.array(reportPointSchema)
})
export type ReportSummary = z.infer<typeof reportSummarySchema>
export const dashboardSchema = z.object({
  generatedAt: z.string(),
  expiresAt: z.string(),
  month: z.string(),
  counts: z.array(z.object({ key: z.string(), label: z.string(), value: z.number() })),
  orders: z.object({ current: z.number(), previous: z.number(), year: z.number() }).nullable(),
  customers: z.object({ current: z.number(), previous: z.number(), year: z.number() }).nullable(),
  daily: z.array(z.object({ label: z.string(), rental: z.number(), service: z.number() }))
})
